import { initSentry } from './lib/sentry'
initSentry(); // Must run before React mounts

// i18n side-effect import: initializes i18next before any component renders
// translation-dependent UI.
import './i18n/config'

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import { AuthProvider } from './components/auth/AuthProvider'
import { LocaleProvider } from './components/i18n/LocaleProvider'
import App from './App.tsx'
import { useChildStore } from './store/childStore'
import { useFamilyStore } from './stores/familyStore'
import { recoverFromStaleChunkError } from './components/ErrorBoundary'
import { registerServiceWorker } from './utils/sw-register'
import { openDb } from './lib/idb'
import { drainQueue, enqueueMutation } from './lib/offline-queue'

// Boot-time cleanup: permanently delete any trashed children whose 7-day
// recovery window has expired. This is the only path that actually purges
// IDB photos in production; everything else just soft-deletes.
useChildStore.getState().purgeExpiredTrash();

// Boot-time tier catch-up: any child whose calendar age has crossed into a
// higher tier while the app was closed gets bumped to the new tier so the
// content they see matches their birthdate. Achievement-mode kids whose
// effective tier is already AT the calendar tier are a no-op.
useChildStore.getState().catchUpAllToAge();

// Boot-time overlay sync: familyStore is an overlay on childStore (adds
// ordering + per-child prefs + co-op sessions). Its own onRehydrateStorage
// only fires when familyStore itself rehydrates — on a fresh install
// childStore may have already fired an add-child event before familyStore
// hydrates, so we sync once unconditionally here. syncWithChildStore is
// idempotent.
useFamilyStore.getState().syncWithChildStore();

// Re-sync whenever children are added/deleted or the active child changes.
// Keep the handler idempotent — only invoke sync when the relevant slices
// actually changed to avoid churning the familyStore on unrelated updates
// (xp, streaks, completions-driven updates, etc.).
useChildStore.subscribe((state, prev) => {
  const childrenChanged = state.children.length !== prev.children.length;
  const activeChanged = state.activeChildId !== prev.activeChildId;
  if (childrenChanged || activeChanged) {
    useFamilyStore.getState().syncWithChildStore();
  }
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <LocaleProvider>
      <AuthProvider>
        <App />
      </AuthProvider>
    </LocaleProvider>
  </StrictMode>,
)

// In dev mode, unregister any stale service workers so they can't intercept
// requests and cause slow/frozen reloads. registerServiceWorker is a no-op
// in dev already, but old SWs from previous prod builds linger in the browser.
if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(regs => {
    regs.forEach(r => r.unregister());
  });
}

// Register the service worker after React mounts. No-op in dev.
registerServiceWorker();

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (recoverFromStaleChunkError(event.reason)) {
      event.preventDefault();
    }
  });

  window.addEventListener('error', (event) => {
    const error = event.error ?? event.message;
    if (recoverFromStaleChunkError(error)) {
      event.preventDefault();
    }
  });
}

// Offline write-ahead queue wiring. Opening the DB at boot ensures the
// onupgradeneeded path runs before anything tries to enqueue; draining
// once at boot flushes anything left over from a previous session.
void (async () => {
  try {
    await openDb();
    await drainQueue();
  } catch (err) {
    console.warn('[offline] boot init failed', err);
  }
})();

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    void drainQueue();
  });
}

if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event.data as
      | { type: 'WAL_ENQUEUE'; request: { url: string; method: string; headers: Record<string, string>; body: Blob | null } }
      | { type: 'DRAIN_REQUEST' }
      | { type: 'ONLINE_CONFIRMED' }
      | undefined;
    if (!data || typeof data !== 'object') return;
    if (data.type === 'WAL_ENQUEUE') {
      const { url, method, headers, body } = data.request;
      const init: RequestInit = { method, headers };
      if (body && method !== 'GET' && method !== 'HEAD') {
        init.body = body;
      }
      const req = new Request(url, init);
      void enqueueMutation(req);
      return;
    }
    if (data.type === 'DRAIN_REQUEST' || data.type === 'ONLINE_CONFIRMED') {
      void drainQueue();
    }
  });
}
